import React from 'react';
import { Box, Flex, Image, Link, Text, useBreakpointValue } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { AlertCircle, Save } from 'react-feather';
import { useSearchParams } from 'react-router-dom';
import { type ApiError } from 'ts/common/common';
import ClientPageCardHeader from 'ts/common/components/ClientPageCardHeader';
import QuestionnaireCompletedView from 'ts/common/components/questionnaire/QuestionnaireCompletedView';
import QuestionnairePageForm from 'ts/common/components/questionnaire/QuestionnairePageForm';
import { useTranslate } from 'ts/common/hooks';
import { formatDate } from 'ts/common/questionnaire/common';

interface Props {
    error: Nullable<ApiError>;
    onGoBack?: () => unknown;
    onSave: (questionId: number, responseText: string | null) => Promise<unknown>;
    onSubmit: () => unknown | Promise<unknown>;
    questionnaire: SpApi.Client.IQuestionnaire;
    requestFailed: boolean;
    saving: boolean;
    submitting?: boolean;
    isPreview?: boolean;
}

const defaultPageIndex = 0;

const Questionnaire: React.FC<Props> = ({
    error,
    onGoBack,
    onSave,
    onSubmit,
    questionnaire,
    requestFailed,
    saving,
    submitting,
    isPreview = false
}) => {
    const t = useTranslate('clientQuestionnaire');
    const isLargeScreen = useBreakpointValue({ base: false, lg: true });
    const [searchParams] = useSearchParams();

    const isCompleted = questionnaire.questionnaireStatus === 'completed';
    const sortedPages = [...questionnaire.pages].sort((a, b) => a.displayOrder - b.displayOrder);

    let pageIndex: number;

    if (isPreview) {
        pageIndex = defaultPageIndex;
    } else {
        let resolvedIndex: number;

        if (searchParams.has('page')) {
            resolvedIndex = Number(searchParams.get('page')) - 1;
        } else {
            const reversedLastPageIndexWithResponses = [...sortedPages]
                .reverse()
                .findIndex((page) =>
                    page.questions.some((question) => question.responseText !== null)
                );

            resolvedIndex =
                reversedLastPageIndexWithResponses === -1
                    ? defaultPageIndex
                    : sortedPages.length - 1 - reversedLastPageIndexWithResponses;
        }

        pageIndex = sortedPages[resolvedIndex] ? resolvedIndex : defaultPageIndex;
    }

    return (
        <Flex direction="column" gap="24px" marginBottom="24px">
            <ClientPageCardHeader
                headingText={questionnaire.name}
                statusText={
                    questionnaire.questionnaireStatus
                        ? t(`statuses.${questionnaire.questionnaireStatus}`)
                        : undefined
                }
            />
            <Flex flexDirection="column" gap="8px">
                <Flex flexDirection="row" flexWrap="wrap" alignItems="flex-start" gap="30px">
                    <Flex
                        as="dl"
                        margin={0}
                        fontSize="14px"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={{ base: '40px', md: '80px' }}
                        alignItems="flex-start"
                    >
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('fromLabel')}
                            </Text>
                            <Text as="dd">{questionnaire.brandName}</Text>
                        </Flex>
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('toLabel')}
                            </Text>
                            <Text as="dd">{questionnaire.contact.name}</Text>
                        </Flex>
                        {questionnaire.dueDate && (
                            <Flex flexDirection="column" gap="4px">
                                <Text as="dt" fontWeight="600" marginBottom={0}>
                                    {t('dueDateLabel')}
                                </Text>
                                <Text as="dd">{formatDate(questionnaire.dueDate, 'UTC')}</Text>
                            </Flex>
                        )}
                        {questionnaire.questionnaireStatus === 'completed' &&
                            !!questionnaire.completed && (
                                <Flex flexDirection="column" gap="4px">
                                    <Text as="dt" fontWeight="600" marginBottom={0}>
                                        {t('submittedOnLabel')}
                                    </Text>
                                    <Text as="dd">{formatDate(questionnaire.completed)}</Text>
                                </Flex>
                            )}
                        {questionnaire.linkedSession && (
                            <Flex flexDirection="column" gap="4px">
                                <Text as="dt" fontWeight="600" marginBottom={0}>
                                    {t('linkedSessionLabel')}
                                </Text>
                                <Text as="dd">
                                    <Link
                                        href={questionnaire.linkedSession.confirmationUrl}
                                        isExternal
                                    >
                                        {questionnaire.linkedSession.name}
                                    </Link>
                                </Text>
                            </Flex>
                        )}
                    </Flex>
                    {questionnaire.brandTheme?.emailBrandLogo?.displayUrl?.original && (
                        <Box
                            flex="1 1 200px"
                            minW="200px"
                            display="flex"
                            justifyContent={{ base: 'center', lg: 'flex-end' }}
                            alignItems="center"
                        >
                            <Image
                                src={questionnaire.brandTheme.emailBrandLogo.displayUrl.original}
                                alt={
                                    questionnaire.brandTheme.logoAltText || questionnaire.brandName
                                }
                                maxWidth="100%"
                                maxH="200px"
                                objectFit="contain"
                                loading="lazy"
                            />
                        </Box>
                    )}
                    {!isCompleted &&
                        isLargeScreen &&
                        (saving || questionnaire.questionnaireStatus === 'partial') && (
                            <Flex
                                flexDirection="row"
                                flexWrap="nowrap"
                                alignItems="center"
                                gap="8px"
                                width="120px"
                                color={requestFailed ? 'red' : 'inherit'}
                            >
                                {saving || !requestFailed ? <Save /> : <AlertCircle />}
                                <Text color="inherit">
                                    {saving
                                        ? t('saving')
                                        : requestFailed
                                        ? t('error')
                                        : t('draftSaved')}
                                </Text>
                            </Flex>
                        )}
                </Flex>
                {questionnaire.descriptionHtml && (
                    <Box
                        color="text"
                        fontFamily="open-sans"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(questionnaire.descriptionHtml)
                        }}
                    />
                )}
            </Flex>
            {isCompleted ? (
                <QuestionnaireCompletedView sortedPages={sortedPages} />
            ) : (
                sortedPages[pageIndex] && (
                    <QuestionnairePageForm
                        error={error}
                        onGoBack={onGoBack}
                        onSave={onSave}
                        onSubmit={onSubmit}
                        pageIndex={pageIndex}
                        questionnairePage={sortedPages[pageIndex]}
                        requestFailed={requestFailed}
                        submitting={submitting}
                        isPreview={isPreview}
                        totalPages={questionnaire.pages.length}
                    />
                )
            )}
        </Flex>
    );
};

export default Questionnaire;
